import React, { useState } from "react";
import { CheckCircle, Edit3, FileText, FileX, MessageSquarePlus, MessageSquare, Flag, FlagOff, Eraser, ChevronDown, ChevronUp, History } from "lucide-react";
import { groupByAssessmentRound } from "../utils/assessmentRoundGroups";

// Per-question counterpart to AssessmentActivityLog.js's whole-assessment action set (assigned/
// reassigned/completed/…) — these are the Action values EnterpriseAssessmentsController.AddResponses
// emits when a question's answer/notes/flag change or a document is uploaded for it.
const QUESTION_ACTION_CONFIG = {
  question_answered:       { icon: CheckCircle,       color: "text-green-500", label: "Answered" },
  question_answer_changed: { icon: Edit3,              color: "text-amber-500", label: "Answer changed" },
  // Emitted when a question is hidden by its parent's answer and its stored answer, note, flag and
  // documents are removed with it — without it the entries above it read as if they still stood.
  question_cleared:        { icon: Eraser,             color: "text-gray-400",  label: "Cleared — question hidden by an earlier answer" },
  note_added:               { icon: MessageSquarePlus, color: "text-blue-500",  label: "Note added" },
  note_changed:              { icon: MessageSquare,      color: "text-amber-500", label: "Note changed" },
  document_uploaded:        { icon: FileText,           color: "text-cyan-600",  label: "Document uploaded" },
  document_removed:         { icon: FileX,              color: "text-gray-400",  label: "Document removed" },
  document_type_changed:    { icon: Edit3,              color: "text-cyan-600",  label: "Document type changed" },
  flagged:                   { icon: Flag,                color: "text-red-500",   label: "Flagged for review", noDetail: true },
  unflagged:                  { icon: FlagOff,              color: "text-gray-400",  label: "Unflagged", noDetail: true },
};

const formatDateTime = (ts) =>
  new Date(ts).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });

// Only Enterprise requests the log with includeChain, so only its entries carry earlier rounds;
// every other flow gets a single unlabelled group and renders exactly as before.
const HistoryGroups = ({ entries }) =>
  groupByAssessmentRound(entries).map((group) => (
    <div key={group.key}>
      {group.label && (
        <div className="text-[11px] font-medium text-gray-500 pt-2 pb-0.5 border-b border-gray-100">{group.label}</div>
      )}
      {group.entries.map((e) => <HistoryEntry key={e.id} entry={e} />)}
    </div>
  ));

const HistoryEntry = ({ entry }) => {
  const cfg = QUESTION_ACTION_CONFIG[entry.action] ?? { icon: Edit3, color: "text-gray-400", label: entry.action };
  const Icon = cfg.icon;

  // document_uploaded/document_removed's "newValue" is a filename, not a value that had a
  // "previous" state — shown plainly rather than as an old→new pair. flagged/unflagged's
  // "True"/"False" values aren't meaningful to a reader, so the label alone ("Flagged for
  // review") carries the whole story.
  let detail = null;
  if (cfg.noDetail) {
    detail = null;
  } else if (entry.action === "question_cleared") {
    detail = entry.previousValue != null ? `was "${entry.previousValue}"` : null;
  } else if (entry.action === "document_uploaded" || entry.action === "document_removed") {
    detail = entry.newValue;
  } else if (entry.previousValue != null) {
    detail = `"${entry.previousValue}" → "${entry.newValue ?? ""}"`;
  } else if (entry.newValue != null) {
    detail = `"${entry.newValue}"`;
  }

  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
      <div className="flex-1 min-w-0">
        <span className="text-xs text-gray-600">
          <span className="font-medium text-gray-700">{cfg.label}</span> by {entry.performedByUserName}
          {detail && <span className="block text-gray-500 break-words">{detail}</span>}
        </span>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{formatDateTime(entry.timestamp)}</span>
    </div>
  );
};

// Always-open variant for a persistent side panel (see EnterpriseAssessment.js's right column) —
// visible without an extra click, and internally scrollable so a long log grows inside its own
// fixed-height box instead of pushing the page taller. Kept as a separate component (not a prop
// toggling QuestionHistoryToggle's layout) since a sidebar panel and an inline collapsible list
// want different chrome (bordered card + sticky vs. a plain expand/collapse line).
export function QuestionHistoryPanel({ entries = [] }) {
  const sorted = [...entries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
        <History className="h-3.5 w-3.5 text-gray-400" />
        History
        {sorted.length > 0 && (
          <span className="ml-auto px-1.5 py-0.5 bg-gray-100 rounded-full text-gray-600 font-normal normal-case">
            {sorted.length}
          </span>
        )}
      </div>
      <div className="max-h-[60vh] overflow-y-auto pr-1">
        {sorted.length === 0 ? (
          <p className="text-xs text-gray-400 py-1">No activity yet for this question.</p>
        ) : (
          <HistoryGroups entries={sorted} />
        )}
      </div>
    </div>
  );
}

// Purely presentational — takes this question's already-fetched, already-filtered log entries as
// a prop. The assessment page fetches the whole assessment's activity log ONCE (an assessment can
// have 50-150 questions; a toggle-per-question fetch would be N+1 network calls) and groups it by
// questionId, handing each question's slice down to its own toggle instance.
export default function QuestionHistoryToggle({ entries = [] }) {
  const [open, setOpen] = useState(false);

  if (entries.length === 0) return null;

  const sorted = [...entries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        History
        <span className="ml-1 px-1.5 py-0.5 bg-gray-100 rounded-full text-gray-600">{sorted.length}</span>
      </button>

      {open && (
        <div className="mt-1 pl-1 border-l-2 border-gray-100">
          <HistoryGroups entries={sorted} />
        </div>
      )}
    </div>
  );
}
